import { z } from "zod";
import { slugString } from "./slug.js";

// WEEKLY TARGETS — the athlete-level training targets, split out of
// program.ts (SP-PROGRAM-SCHEMA). program.ts had reached 479 lines and
// carried a TODO naming exactly this boundary: the targets concept had
// already migrated from the program to the athlete (ME-0/ME-3), so it was
// no longer program schema at all, only program-adjacent.
//
// Dependency direction is one-way and must stay that way: program.ts
// imports ProgramTargets from here (Program.targets), and re-exports this
// whole module, so every existing `./schemas/program.js` import keeps
// resolving. Nothing here may import from program.ts — these are zod
// schemas evaluated at module load, and a cycle would be a TDZ crash at
// import time rather than a type error.

// WEEKLY TARGETS (AP-1) — program-level training targets the athlete or
// coach prescribes as WEEKLY AGGREGATES ("N hard sets of squat pattern per
// week", "M kg of quad tonnage per week"). Distinct from SetIntensity, which
// is the per-SET prescription; these are the weekly ROLL-UP the three-zone
// level meter (AP-3) scores actual training against. v1 is a single static
// weekly target set — per-cycle-week undulation is a later slice.
//
// Axes (2026-07-07, use-driven rework — pattern demoted): `muscle` →
// CatalogMuscleSlug (the default; absorbs exercise rotation because
// volumeByMuscle weights by contribution, and a hard set of any squat variant
// counts toward quads); `exercise` → an exercise slug (only meaningful for a
// FIXED lift you don't rotate — targeted from the program's own exercises).
// `pattern` → movementPattern is KEPT for back-compat with already-saved
// targets but the editor no longer offers it: a pattern like "squat" lumps
// hack/belt/back squat (very different loads) and is meaningless for tonnage,
// and isolation/cardio are too generic. See [[reference_jl_targets_editor]].
export const TargetAxis = z.enum(["pattern", "muscle", "exercise"]);
export type TargetAxis = z.infer<typeof TargetAxis>;

export const TargetMetric = z.enum(["hard-sets", "tonnage", "reps"]);
export type TargetMetric = z.infer<typeof TargetMetric>;

export const TargetItem = z.object({
  axis: TargetAxis,
  // A movementPattern value (axis=pattern) or CatalogMuscleSlug (axis=muscle).
  // Kept a bounded slug string — the editor's picker constrains it to valid
  // vocab; an unknown key just scores 0 at the meter, never throws (a custom
  // exercise with no pattern buckets to `other`, the model tolerates it).
  key: slugString(40),
  metric: TargetMetric,
  // Weekly goal in the metric's unit: hard-sets = set count, tonnage = kg,
  // reps = rep count. Positive — a 0 target is "no target", omit the item.
  value: z.number().gt(0).max(1_000_000),
  // In-target BAND as MULTIPLIERS of `value` (not absolutes): the meter's
  // middle "in-target" zone, e.g. [0.9, 1.1] = ±10%. A band (not a line)
  // avoids decimal-precision anxiety at 108%. Optional — `targetBand()`
  // applies the shared default when absent. Constraint: 0 < low ≤ high.
  band: z
    .tuple([z.number(), z.number()])
    .refine(([lo, hi]) => lo > 0 && lo <= hi, {
      message: "band must be [low, high] with 0 < low ≤ high",
    })
    .optional(),
});
export type TargetItem = z.infer<typeof TargetItem>;

// No two items may target the same (axis, key, metric) — contradictory
// weekly goals for one bucket would make the meter ambiguous. ONE predicate,
// shared by the program shape (≥1 item) and the athlete shape (≥0), so the
// uniqueness rule can never drift between the two homes of the same concept.
const targetItemsUnique = (items: TargetItem[]): boolean =>
  new Set(items.map((i) => `${i.axis}:${i.key}:${i.metric}`)).size ===
  items.length;
const DUPLICATE_TARGET_MESSAGE = {
  message: "duplicate target (axis, key, metric)",
} as const;

// The wire / program value: 1..40 unique items. "No targets" is expressed as
// absence (null on the wire, an omitted field on the program), NEVER an empty
// array — an empty array would make "clear my targets" indistinguishable from
// a validation slip.
export const weeklyTargetItems = z
  .array(TargetItem)
  .min(1)
  .max(40)
  .refine(targetItemsUnique, DUPLICATE_TARGET_MESSAGE);

export const ProgramTargets = z.object({
  // v1: only weekly-aggregate scope. Explicit literal so a future scope
  // (block, session) is an additive enum, not a breaking reshape.
  scope: z.literal("weekly"),
  items: weeklyTargetItems,
});
export type ProgramTargets = z.infer<typeof ProgramTargets>;

// ME-0 — the ATHLETE's live weekly targets, one singleton document per athlete
// (partition USER#<sub>, sk ATHLETETARGETS). Same value shape as
// ProgramTargets, deliberately, so every scorer/editor speaks one vocabulary —
// but with three differences that follow from the target belonging to the
// person, not the plan:
//   1. exactly one owner ⇒ no fan-out, no multi-assignee ambiguity (the
//      structural wrinkle CO-7 had to reject disappears by construction);
//   2. items may be EMPTY here — a persisted "cleared" state — where the
//      program/wire shape forbids it, because the singleton doc always exists
//      once touched and the meter reads it directly (never `null` for "no
//      program", the finto-baco §7 resolves);
//   3. it carries its own `updatedAt`: the optimistic-lock validator
//      (athleteTargetsEtag), the same mechanism as programEtag, different home.
// [[project_jl_me_hub_athlete_targets]]
export const athleteTargetItems = z
  .array(TargetItem)
  .min(0)
  .max(40)
  .refine(targetItemsUnique, DUPLICATE_TARGET_MESSAGE);

export const AthleteTargets = z.object({
  scope: z.literal("weekly"),
  items: athleteTargetItems,
  updatedAt: z.iso.datetime(),
});
export type AthleteTargets = z.infer<typeof AthleteTargets>;

// The stand-in `updatedAt` for an athlete who has never saved a target doc.
// GET /me/targets synthesizes it so the FIRST PATCH still carries a real
// If-Match and the create-if-absent lock has a stable prior to compare
// against — no special first-write branch on the client. Epoch: it sorts
// before every real ISO timestamp and can never collide with one.
export const ATHLETE_TARGETS_ABSENT_UPDATED_AT = "1970-01-01T00:00:00.000Z";

// Shared default in-target band (±10%). ONE place so the meter, editor, and
// any scorer agree — never re-pick these multipliers inline.
export const DEFAULT_TARGET_BAND: readonly [number, number] = [0.9, 1.1];
export const targetBand = (item: TargetItem): [number, number] =>
  item.band ?? [DEFAULT_TARGET_BAND[0], DEFAULT_TARGET_BAND[1]];

// CO-7 — one append-on-change record of `Program.targets`, living in the
// ATHLETE's partition (not on the program doc: programs fan out one row
// per assignee and are written with a full-replace Put, so an embedded
// list would be wiped by the athlete's next ordinary save).
//
// ⚠︎ WHY THIS EXISTS AT ALL — it is not optional polish. The moment a
// coach can adjust targets mid-block, the target becomes a TIME SERIES.
// Without this log, `computeWeeklyTargets` scores every past week
// against TODAY's target, so history silently re-baselines on every
// edit and an adherence view lies about the past. Offline sync makes it
// worse, not better: sets synced days late land with their true
// timestamps and would be judged against a target that didn't exist
// yet. Not backfillable — the prior values are simply gone. Same
// constraint binds AP-4 and AP-1c.
//
// A full SNAPSHOT per entry, like Profile.painHistory — not a delta.
// Deltas compound: one lost or duplicated entry corrupts everything
// after it, whereas a snapshot is self-sufficient. The "12 → 14"
// reading in the UI derives from consecutive snapshots at display time.
//
// SERVER-AUTHORED ONLY. The client never sends this; the targets route
// appends it as a side-effect of the write it just made. It is the
// audit trail for coach writes (who changed the athlete's targets, and
// when) — a record the subject could edit would be worth nothing.
export const TargetHistoryEntry = z.object({
  id: z.string().min(1),
  // The program active when this change was made — CONTEXT, not the key.
  // ME-0: the target belongs to the athlete now, so a self-edit from the Me
  // surface has no program in scope. Was required and program-keyed under
  // CO-7; kept as an optional breadcrumb ("which program was active when you
  // moved this") that the athlete-keyed history no longer needs to function.
  programId: z.string().min(1).optional(),
  at: z.iso.datetime(),
  // The sub that made the change: the athlete themselves, or a coach
  // holding "write:targets". Role isn't stored — it's derivable
  // (`bySub === athleteSub` ⇒ self), and a stored role could disagree
  // with the edge that actually authorised the write.
  bySub: z.string().min(1).max(128),
  // The targets AFTER this change. Absent = targets were cleared.
  targets: ProgramTargets.optional(),
});
export type TargetHistoryEntry = z.infer<typeof TargetHistoryEntry>;

// Wire shape for PATCH /api/me/targets (ME-0). The key is REQUIRED and the
// value nullable (null = clear them): an optional key would make "clear my
// targets" indistinguishable from "I forgot to send the field", which is
// exactly how a clearable field silently stops clearing
// ([[feedback_catalog_optional_field_must_always_send]]).
//
// Was PatchProgramTargets, aliased under this name once the target's home
// moved from the program to the athlete (ME-0). The program-first
// PATCH /:id/targets route it served is retired (ME-3), leaving the old name
// with zero call sites, so the alias indirection was dropped and this is now
// the sole declaration.
// (The TODO that used to sit here asked for exactly this split — "consider
// splitting AthleteTargets/TargetHistoryEntry out of program.ts into their
// own schema module". Done: that is this file. It rode along in the move,
// where it would have read as a request to split the split.)
export const PatchAthleteTargets = z.object({
  targets: ProgramTargets.nullable(),
});
export type PatchAthleteTargets = z.infer<typeof PatchAthleteTargets>;
